import { NgIf } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { BoondockFacade } from '@curbnturf/boondock-listing';
import { SiteSvgsComponent } from '@curbnturf/custom-svgs/src/lib/site-svgs/site-svgs.component';
import { Amenity, AmenityType, Boondock } from '@curbnturf/entities';
import { IAmenitiesRatingForm } from '@curbnturf/form-helpers';
import { RatingFacade } from '@curbnturf/rating';
import * as RatingActions from '@curbnturf/rating/src/lib/+state/rating.actions';
import { LoadingController, NavController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subscription } from 'rxjs';
import { RatingAvailabilityToggleComponent } from '../rating-availability-toggle/rating-availability-toggle.component';

@Component({
  selector: 'curbnturf-rating-boondock-amenities',
  templateUrl: './rating-boondock-amenities.component.html',
  styleUrls: ['./rating-boondock-amenities.component.scss'],
  standalone: true,
  imports: [FormsModule, NgIf, RatingAvailabilityToggleComponent, ReactiveFormsModule, RouterModule, SiteSvgsComponent],
})
export class RatingBoondockAmenitiesComponent implements OnInit, OnDestroy {
  @Output() navigateBack: EventEmitter<void> = new EventEmitter<void>();

  @Output() continue: EventEmitter<void> = new EventEmitter<void>();

  @Output() skip: EventEmitter<void> = new EventEmitter<void>();

  subscriptions: Subscription[] = [];

  boondock$: Observable<Boondock | null>;
  boondockId: number | undefined;

  appContext: boolean;
  caretakerBoondock: number[];

  amenitiesForm: FormGroup<IAmenitiesRatingForm>;

  get isCaretaker() {
    return !!(this.boondockId && this.caretakerBoondock.includes(this.boondockId));
  }

  constructor(
    private fb: FormBuilder,
    private boondockFacade: BoondockFacade,
    private route: ActivatedRoute,
    private navCtrl: NavController,
    private loadingController: LoadingController,
    private updates$: Actions,
    private ratingFacade: RatingFacade,
  ) {}

  ngOnInit(): void {
    this.amenitiesForm = this.fb.group({
      electricity: '',
      potableWater: '',
      dumpStation: '',
      sewage: '',
      cellPhoneCoverage: '',
      wifi: '',
      firesAllowed: '',
      petsAllowed: '',
      trashProvided: '',
      recycleProvided: '',
      picnicTable: '',
      toiletAvailable: '',
      shower: '',
      laundry: '',
      tentsAllowed: '',
      vehiclesAllowed: '',
      pool: '',
    });

    this.boondock$ = this.boondockFacade.selected$;
    this.subscriptions.push(
      this.ratingFacade.caretakerSites$.subscribe((boondocks) => {
        this.caretakerBoondock = boondocks;
      }),
      this.boondock$.subscribe((boondock) => {
        if (boondock) {
          this.boondockId = boondock.id;
          this.populateFields(boondock);
        }
      }),
      this.route.data.subscribe((data: { appContext: boolean }) => {
        this.appContext = data.appContext || false;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  populateFields(boondock: Boondock) {
    if (this.isCaretaker) {
      this.amenitiesForm.patchValue({
        electricity: boondock.amenities?.find((el) => el.type === 'electricity') ? 'yes' : 'no',
        potableWater: boondock.amenities?.find((el) => el.type === 'water') ? 'yes' : 'no',
        dumpStation: boondock.amenities?.find((el) => el.type === 'dump') ? 'yes' : 'no',
        sewage: boondock.amenities?.find((el) => el.type === 'sewage') ? 'yes' : 'no',
        cellPhoneCoverage: boondock.amenities?.find((el) => el.type === 'cell') ? 'yes' : 'no',
        wifi: boondock.amenities?.find((el) => el.type === 'wifi') ? 'yes' : 'no',
        firesAllowed: boondock.amenities?.find((el) => el.type === 'firesAllowed') ? 'yes' : 'no',
        petsAllowed: boondock.amenities?.find((el) => el.type === 'petsAllowed') ? 'yes' : 'no',
        trashProvided: boondock.amenities?.find((el) => el.type === 'trash') ? 'yes' : 'no',
        recycleProvided: boondock.amenities?.find((el) => el.type === 'recycling') ? 'yes' : 'no',
        picnicTable: boondock.amenities?.find((el) => el.type === 'table') ? 'yes' : 'no',
        toiletAvailable: boondock.amenities?.find((el) => el.type === 'restroom') ? 'yes' : 'no',
        shower: boondock.amenities?.find((el) => el.type === 'shower') ? 'yes' : 'no',
        laundry: boondock.amenities?.find((el) => el.type === 'laundry') ? 'yes' : 'no',
        tentsAllowed: boondock.amenities?.find((el) => el.type === 'tents') ? 'yes' : 'no',
        vehiclesAllowed: boondock.amenities?.find((el) => el.type === 'vehicles') ? 'yes' : 'no',
        pool: boondock.amenities?.find((el) => el.type === 'pool') ? 'yes' : 'no',
      });
    }
  }

  async onContinue() {
    const amenities = [];

    for (const amenitiesKey in this.amenitiesForm.value) {
      if (this.amenitiesForm.value.hasOwnProperty(amenitiesKey)) {
        const amenity = (this.amenitiesForm.value as any)[amenitiesKey];
        if (amenity === 'yes' || amenity === 'no') {
          amenities.push(
            new Amenity({
              type: amenitiesKey as keyof typeof AmenityType,
              description: 'User response: ' + amenity,
              boondock: { id: this.boondockId },
            }),
          );
        }
      }
    }

    const loading = await this.loadingController.create({
      message: 'Saving...',
      duration: 2000,
    });
    await loading.present();

    // Make the subscription before sending the request to make sure we don't miss anything.
    this.subscriptions.push(
      this.updates$.pipe(ofType(RatingActions.selectCaretakerBoondock)).subscribe(async () => {
        await loading.dismiss();

        // Emit event for web and navigate for app.
        if (this.appContext) {
          await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'terrain']);
        } else {
          this.continue.emit();
        }
      }),
    );

    this.ratingFacade.selectCaretakerBoondock({
      boondockId: this.boondockId,
      boondock: {
        id: this.boondockId,
        amenities,
      },
    });
  }

  async onSkip() {
    // Emit event for web and navigate for app.
    if (this.appContext) {
      await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'terrain']);
    } else {
      this.skip.emit();
    }
  }
}
